import React, { useState, useEffect } from 'react';
import { Card, Button, Row, Col, Typography, Badge, Modal } from 'antd';
import { 
  ShoppingCartOutlined,
  ArrowLeftOutlined,
  HeartOutlined,
  SafetyOutlined,
  BulbOutlined
} from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import StripeCheckout from '../../components/checkout/StripeCheckout';
import { trackProductView, trackAddToCart, trackPurchase } from '../../../utils/analytics';
import calmiImage from '../../../assets/images/Calmi.png';

const { Title, Text, Paragraph } = Typography;

const CalmiGentle = () => {
  const history = useHistory();
  const [cartItems, setCartItems] = useState([]);
  const [checkoutVisible, setCheckoutVisible] = useState(false);

  useEffect(() => {
    trackProductView('lumi-gentle', 'Lumi Gentle', 499);
  }, []);

  const product = {
    id: 'lumi-gentle',
    name: 'Lumi Gentle',
    price: 499,
    image: calmiImage,
    collection: 'Core Collection',
    subtitle: 'Cloud-soft comfort bear',
    description: 'Ultra-plush cream-colored bear with extra-soft fur and a lighter feel. Designed for children who prefer gentler textures and softer hugs.',
    fullDescription: 'Lumi Gentle is specially crafted for sensitive souls who need a more delicate touch. The ultra-plush cream-colored exterior provides the softest possible texture, while the lighter weighted core offers gentle pressure without overwhelming sensitive children. Perfect for kids with sensory processing differences or those who are just beginning their comfort journey.',
    specs: { 
      dimensions: '12" x 8" x 6"', 
      color: 'Cream', 
      texture: 'Ultra-Soft Plush',
      batteryLife: '8+ hours',
      charging: 'USB-C',
      ageRange: '4-12 years'
    },
    features: [
      'Ultra-soft cream plush exterior for sensitive skin',
      'Lighter weighted core perfect for gentle pressure',
      'Ideal for children with sensory sensitivities',
      'Pastel cream color creates calming visual environment',
      'Same AI intelligence with gentler interaction style',
      'Perfect for bedtime and quiet comfort moments'
    ],
    idealFor: [
      'Highly sensitive children',
      'Kids new to weighted products',
      'Children with autism spectrum traits',
      'Gentle bedtime routines',
      'Sensory processing support',
      'Creating calm, peaceful environments'
    ]
  };

  const addToCart = () => {
    trackAddToCart(product.id, product.name, product.price);
    setCartItems([{ ...product, quantity: 1 }]);
    setCheckoutVisible(true);
  };

  const handlePaymentSuccess = (paymentIntent) => {
    trackPurchase(paymentIntent.id, [{ id: product.id, name: product.name, price: product.price, quantity: 1 }], product.price);
    setCheckoutVisible(false);
    setCartItems([]);
    Modal.success({
      title: 'Preorder Confirmed! 🎉',
      content: (
        <div>
          <p>Your {product.name} preorder has been confirmed! It's guaranteed to arrive in summer 2027.</p>
          <p>You'll receive an email confirmation shortly.</p>
          <p><strong>Order ID:</strong> {paymentIntent.id}</p>
        </div>
      ),
    });
  };

  const handlePaymentError = (error) => {
    Modal.error({
      title: 'Payment Failed',
      content: 'There was an issue processing your payment. Please try again.',
    });
  };

  return (
    <div style={{ minHeight: '100vh', background: '#fff' }}>
      {/* Header */}
      <div style={{ 
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
        padding: '20px 0',
        marginBottom: '40px'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <Button 
            type="text" 
            icon={<ArrowLeftOutlined />} 
            onClick={() => history.push('/')}
            style={{ color: '#6c757d', marginBottom: '10px' }}
          >
            Back to Lumi Family
          </Button>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ fontSize: '32px', marginRight: '15px' }}>🤍</span>
            <div>
              <Title level={2} style={{ color: '#495057', margin: 0 }}>
                {product.name}
              </Title>
              <Text style={{ color: '#6c757d', fontSize: '18px' }}>
                {product.subtitle}
              </Text>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
        {/* Product Overview */}
        <Row gutter={[48, 32]} style={{ marginBottom: '60px' }}>
          <Col xs={24} lg={12}>
            <div style={{ 
              textAlign: 'center', 
              padding: '40px', 
              backgroundColor: '#fefefe', 
              borderRadius: '12px', 
              height: '500px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              border: '2px solid #f8f9fa'
            }}>
              <img 
                src={product.image} 
                alt={product.name}
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '100%', 
                  objectFit: 'contain',
                  filter: 'brightness(1.1) contrast(0.9)'
                }}
              />
            </div>
          </Col>
          <Col xs={24} lg={12}>
            <Badge.Ribbon text={product.collection} color="geekblue">
              <Card>
                <Title level={1} style={{ marginTop: 0 }}>
                  {product.name}
                </Title>
                <Title level={2} style={{ color: '#e74c3c', marginBottom: '20px' }}>
                  ${product.price} <Text style={{ fontSize: '16px', color: '#7f8c8d' }}>Preorder · Guaranteed delivery in summer 2027</Text>
                </Title>
                <Paragraph style={{ fontSize: '18px', marginBottom: '30px' }}>
                  {product.fullDescription}
                </Paragraph>
                
                <Button
                  type="primary"
                  size="large"
                  icon={<ShoppingCartOutlined />}
                  onClick={addToCart}
                  block
                  style={{
                    background: '#e74c3c',
                    borderColor: '#e74c3c',
                    height: '60px',
                    fontSize: '20px',
                    fontWeight: 'bold',
                    marginBottom: '20px'
                  }}
                >
                  Preorder Now - ${product.price}
                </Button>

                <Card size="small" style={{ backgroundColor: '#f0f8ff' }}>
                  <Text strong>📅 Preorder Details:</Text>
                  <ul style={{ marginTop: '8px', marginBottom: 0 }}>
                    <li>✨ Preordering guarantees your Lumi in summer 2027</li>
                    <li>💳 Pay today to reserve yours</li>
                    <li>🔄 Cancel anytime before shipping</li>
                  </ul>
                </Card>
              </Card>
            </Badge.Ribbon>
          </Col>
        </Row>

        {/* Specifications */}
        <Card title="Technical Specifications" style={{ marginBottom: '40px' }}>
          <Row gutter={[24, 16]}>
            <Col xs={12} md={6}>
              <Text strong>Weight:</Text><br/>
              <Text>{product.specs.weight}</Text>
            </Col>
            <Col xs={12} md={6}>
              <Text strong>Dimensions:</Text><br/>
              <Text>{product.specs.dimensions}</Text>
            </Col>
            <Col xs={12} md={6}>
              <Text strong>Color:</Text><br/>
              <Text>{product.specs.color}</Text>
            </Col>
            <Col xs={12} md={6}>
              <Text strong>Texture:</Text><br/>
              <Text>{product.specs.texture}</Text>
            </Col>
            <Col xs={12} md={6}>
              <Text strong>Battery Life:</Text><br/>
              <Text>{product.specs.batteryLife}</Text>
            </Col>
            <Col xs={12} md={6}>
              <Text strong>Charging:</Text><br/>
              <Text>{product.specs.charging}</Text>
            </Col>
          </Row>
        </Card>

        {/* Features */}
        <Row gutter={[32, 32]} style={{ marginBottom: '60px' }}>
          <Col xs={24} md={12}>
            <Card title="Key Features" style={{ height: '100%' }}>
              <ul>
                {product.features.map((feature, index) => (
                  <li key={index} style={{ marginBottom: '8px', fontSize: '16px' }}>
                    {feature}
                  </li>
                ))}
              </ul>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card title="Ideal For" style={{ height: '100%' }}>
              <ul>
                {product.idealFor.map((use, index) => (
                  <li key={index} style={{ marginBottom: '8px', fontSize: '16px' }}>
                    {use}
                  </li>
                ))}
              </ul>
            </Card>
          </Col>
        </Row>

        {/* Benefits */}
        <Title level={2} style={{ textAlign: 'center', marginBottom: '40px' }}>
          Why Choose Lumi Gentle?
        </Title>
        <Row gutter={[32, 32]} style={{ marginBottom: '60px' }}>
          <Col xs={24} md={8}>
            <Card style={{ textAlign: 'center', height: '100%' }}>
              <HeartOutlined style={{ fontSize: '48px', color: '#ffc0cb', marginBottom: '16px' }} />
              <Title level={4}>Sensory-Friendly</Title>
              <Text>
                Designed specifically for children with sensory sensitivities and processing differences.
              </Text>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card style={{ textAlign: 'center', height: '100%' }}>
              <SafetyOutlined style={{ fontSize: '48px', color: '#e6f3ff', marginBottom: '16px' }} />
              <Title level={4}>Gentle Pressure</Title>
              <Text>
                Light weighted core provides comfort without overwhelming sensitive children.
              </Text>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card style={{ textAlign: 'center', height: '100%' }}>
              <BulbOutlined style={{ fontSize: '48px', color: '#fff2e6', marginBottom: '16px' }} />
              <Title level={4}>Calming Colors</Title>
              <Text>
                Soft cream tones create a peaceful, non-stimulating visual environment.
              </Text>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Checkout Modal */}
      <Modal
        title=""
        open={checkoutVisible}
        onCancel={() => setCheckoutVisible(false)}
        footer={null}
        width={900}
        style={{ top: 20 }}
        destroyOnClose
      >
        <StripeCheckout
          cartItems={cartItems}
          onPaymentSuccess={handlePaymentSuccess}
          onPaymentError={handlePaymentError}
        />
      </Modal>
    </div>
  );
};

export default CalmiGentle;